import { DataTable } from '@/components/data-table';
import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { deleteQuestionInfo, useGetQuestionListByTopic } from '@/services/generatedAPIs';
import { QuestionInfo } from '@/services/generatedAPIs.schemas';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { ROUTE } from '@/routes/constants';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { getQuestionText, getStandardAnswerText } from '@/lib/utils';
import { convertSpacesToLatex } from '@/utils/preprocess';
import { QuestionOrigin } from '@/utils/constants';

type TableData = QuestionInfo

const TopicEditPage = () => {
  const { t, i18n } = useTranslation();
  const { getCurrentUser } = useAuth();
  const { mutate } = useSWRConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: questionInfoRsp, error, isLoading } = useGetQuestionListByTopic({
    topicIds: [location.state.topicId],
    libraryNames: QuestionOrigin.map(value => value.originName),
    includePress: true,
  }, { swr: { swrKey: 'TopicEditPage-getQuestionListByTopic' } });


  const columns: ColumnDef<TableData>[] = [
    {
      id: "select",
      header: ({ table }) => (
        //   <Checkbox
        //     checked={
        //       table.getIsAllPageRowsSelected() ||
        //       (table.getIsSomePageRowsSelected() && "indeterminate")
        //     }
        //     onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        //     aria-label="Select all"
        //   />
        <></>
      ),
      cell: ({ row }) => (
        //   <Checkbox
        //     checked={row.getIsSelected()}
        //     onCheckedChange={(value) => row.toggleSelected(!!value)}
        //     aria-label="Select row"
        //   />
        <></>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "questionText",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("common.question")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => {
        return (
          <math-field read-only className="w-xs break-all border-gray-200">
            {convertSpacesToLatex(getQuestionText(row.original, i18n))}
          </math-field>
          // <div className='max-w-100 text-wrap'>{row.original.questionText}</div>
        )
      },
    },
    {
      accessorKey: "standardAnswer",
      header: () => { return (<span>{t("common.answer")}</span>) },
      cell: ({ row }) => (
        <math-field read-only className="w-xs break-all border-gray-200">
          {convertSpacesToLatex(getStandardAnswerText(row.original, i18n))}
        </math-field>
        // <div className='max-w-100 text-wrap'>{row.original.standardAnswer}</div>
      ),
    },
    {
      accessorKey: "type",
      header: () => { return (<span>{t("teacher.qtype")}</span>) },
      cell: ({ row }) => (
        <>
          {row.original.questionType === "MCQ" && <div>{t('teacher.mcq')}</div>}
          {row.original.questionType === "FRQ" && <div>{t('teacher.frq')}</div>}
          {row.original.questionType === "BFQ" && <div>{t('teacher.bfq')}</div>}
        </>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const info = row.original;
        const { mutate } = useSWRConfig();

        return (
          <div className='flex place-content-evenly'>
            {
              (getCurrentUser()!.email === info.creatorId) && <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">{t("teacher.archive")}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('teacher.confirmdelete')}</AlertDialogTitle>
                    {/* <AlertDialogDescription>
            This action cannot be undone. This will permanently delete your
            account and remove your data from our servers.
          </AlertDialogDescription> */}
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("component.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={async () => {
                      try {
                        const rsp = await deleteQuestionInfo({ questionInfoId: info.id! });
                        if (rsp.code === 200) {
                          mutate('TopicEditPage-getQuestionListByTopic');
                        }
                      } catch (error) {
                        console.log('deleteQuestionInfo error', error);
                      }
                    }}>{t("teacher.archive")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            }
            <Button variant="outline" onClick={() => {
              navigate(ROUTE.TEACHER.LIBRARY_QUESTION_EDIT, {
                state: { isEditMode: true, questionId: info.id, defaultValue: info, topicId: location.state.topicId, topicName: location.state.topicName }
              })
            }}>{t("teacher.editquestion")}</Button>
          </div>
        )
      },
    },
  ]


  return (
    <ContentLayout title={t("teacher.library")}>
      <div className='p-4'>
        <DataTable data={questionInfoRsp?.data?.questionList[0].filter((questionInfo) => {
          return !questionInfo.archived;
        }) ?? []} columns={columns} filterColumn='questionText' filterName={t('common.question')} renderButton={() => {
          return (
            <div className="flex gap-2">
              <NavLink to={ROUTE.TEACHER.LIBRARY_QUESTION_EDIT} state={{ isEditMode: false, topicId: location.state.topicId, topicName: location.state.topicName }}>
                <Button variant="default" className="bg-primary">{t("teacher.createquestion")}</Button>
              </NavLink>
              <NavLink to={ROUTE.TEACHER.LIBRARY_QUESTION_EDIT} state={{ isEditMode: false, topicId: location.state.topicId, topicName: location.state.topicName, isAIGenerate: true }}>
                <Button variant="default" className="bg-chart-4 hover:bg-chart-4/90">{t("teacher.aigen")}</Button>
              </NavLink>
            </div>
          )
        }} />
      </div>
    </ContentLayout>
  );
};

export default TopicEditPage;